import { useState, type ReactNode } from 'react';
import { Card, ErrorBanner, Loading, Spinner } from '../../components/ui.js';
import { api, type Service } from '../../lib/api.js';
import { formatMoney, formatNumber, formatRate, minorUnitExponent } from '../../lib/format.js';
import { useAction, useAsync } from '../../lib/hooks.js';
import { AdminLayout } from './AdminLayout.js';

interface AdminPlanService {
  serviceKey: string;
  enabled: boolean;
  includedQuantity: number | null;
  overagePriceMinor: number | null;
  overageUnitSize: number;
}

interface AdminPlan {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  currency: string;
  interval: 'month' | 'year';
  basePriceMinor: number;
  version: number;
  sortOrder: number;
  active: boolean;
  contactSalesOnly: boolean;
  services: AdminPlanService[];
}

/**
 * Plan catalog.
 *
 * Prices are not editable in place: repricing a live plan would change what
 * existing subscribers agreed to pay, so a price change is a new version. This
 * screen surfaces every version, including superseded ones, so the history of
 * what was sold stays visible.
 *
 * Allowances are a separate action from a price change, because they are a
 * different kind of edit — see the note on the allowance form.
 */
export function AdminPlansPage(): ReactNode {
  return <AdminLayout>{(staff) => <Plans canWrite={staff.role === 'superadmin'} />}</AdminLayout>;
}

function Plans({ canWrite }: { canWrite: boolean }): ReactNode {
  const plans = useAsync(() => api.get<{ data: AdminPlan[] }>('/api/admin/plans'), []);
  const catalog = useAsync(() => api.get<{ data: Service[] }>('/api/services'), []);
  const { run, pending, error } = useAction();
  const [editing, setEditing] = useState<{ planId: string; mode: EditorMode } | null>(null);

  if (plans.loading) return <Loading />;

  const list = plans.data?.data ?? [];
  const live = list.filter((plan) => plan.active);
  const superseded = list.filter((plan) => !plan.active);
  const services = catalog.data?.data ?? [];

  async function toggleActive(plan: AdminPlan): Promise<void> {
    await run(async () => {
      await api.patch(`/api/admin/plans/${plan.id}`, { active: !plan.active });
      plans.reload();
    });
  }

  function rowProps(plan: AdminPlan): PlanRowProps {
    return {
      plan,
      canWrite,
      pending,
      services,
      onToggle: toggleActive,
      mode: editing?.planId === plan.id ? editing.mode : null,
      onMode: (mode) => setEditing(mode === null ? null : { planId: plan.id, mode }),
      onSaved: () => {
        setEditing(null);
        plans.reload();
      },
    };
  }

  return (
    <div className="stack">
      <ErrorBanner error={plans.error} />
      <ErrorBanner error={catalog.error} />
      <ErrorBanner message={error} />

      <div className="banner banner-warning">
        Prices are immutable once a plan is published. To change one, create a new version — the old
        version is retained so existing subscribers keep what they bought and past invoices stay
        reproducible.
      </div>

      <Card title="Current plans">
        {live.map((plan) => (
          <PlanRow key={plan.id} {...rowProps(plan)} />
        ))}
        {canWrite && live.length > 0 && (
          <p className="field-hint" style={{ marginTop: '0.75rem' }}>
            To introduce a plan that does not exist yet, publish a new version of the closest one
            and give it a new slug.
          </p>
        )}
      </Card>

      {superseded.length > 0 && (
        <Card title="Superseded versions">
          <p className="small faint" style={{ marginBottom: '0.75rem' }}>
            Retained because subscriptions and invoices reference them.
          </p>
          {superseded.map((plan) => (
            <PlanRow key={plan.id} {...rowProps(plan)} />
          ))}
        </Card>
      )}
    </div>
  );
}

type EditorMode = 'allowances' | 'version';

interface PlanRowProps {
  plan: AdminPlan;
  canWrite: boolean;
  pending: boolean;
  services: Service[];
  mode: EditorMode | null;
  onToggle: (plan: AdminPlan) => Promise<void>;
  onMode: (mode: EditorMode | null) => void;
  onSaved: () => void;
}

function PlanRow({
  plan,
  canWrite,
  pending,
  services,
  mode,
  onToggle,
  onMode,
  onSaved,
}: PlanRowProps): ReactNode {
  const metered = plan.services.filter((s) => s.enabled && s.overagePriceMinor !== null);

  return (
    <div className="usage-row">
      <div className="row">
        <div>
          <div className="row" style={{ gap: '0.5rem' }}>
            <strong>{plan.name}</strong>
            <span className="badge">v{plan.version}</span>
            <span className="badge">{plan.currency}</span>
            {plan.contactSalesOnly && <span className="badge">Quoted</span>}
            {!plan.active && <span className="badge badge-danger">Inactive</span>}
          </div>
          <p className="small faint mono">{plan.slug}</p>
        </div>

        <div className="num" style={{ marginLeft: 'auto', fontSize: '1rem' }}>
          {plan.contactSalesOnly ? 'Custom' : formatMoney(plan.basePriceMinor, plan.currency)}
          <span className="faint small"> / {plan.interval}</span>
        </div>

        {canWrite && (
          <div className="button-row">
            <button
              type="button"
              className="button-ghost"
              onClick={() => onMode(mode === 'allowances' ? null : 'allowances')}
            >
              {mode === 'allowances' ? 'Close' : 'Allowances'}
            </button>
            <button
              type="button"
              className="button-ghost"
              onClick={() => onMode(mode === 'version' ? null : 'version')}
            >
              {mode === 'version' ? 'Close' : 'New version'}
            </button>
            <button
              type="button"
              className="button-ghost"
              disabled={pending}
              onClick={() => void onToggle(plan)}
            >
              {plan.active ? 'Deactivate' : 'Reactivate'}
            </button>
          </div>
        )}
      </div>

      {metered.length > 0 && (
        <p className="small faint" style={{ marginTop: '0.4rem' }}>
          {metered
            .map(
              (s) =>
                `${s.serviceKey}: ${
                  s.includedQuantity === null ? 'unlimited' : formatNumber(s.includedQuantity)
                }` +
                (s.overagePriceMinor
                  ? ` then ${formatRate(s.overagePriceMinor, s.overageUnitSize, plan.currency)}`
                  : ''),
            )
            .join(' · ')}
        </p>
      )}

      {mode === 'allowances' && <AllowanceForm plan={plan} services={services} onSaved={onSaved} />}
      {mode === 'version' && <NewVersionForm plan={plan} services={services} onSaved={onSaved} />}
    </div>
  );
}

/* ------------------------------------------------------------------- editor */

/** One editable grant row. Kept as strings because that is what an input holds. */
interface GrantDraft {
  serviceKey: string;
  name: string;
  kind: 'metered' | 'feature';
  unit: string;
  enabled: boolean;
  /** Blank means unlimited. */
  included: string;
  /** Blank means overage is not sold. */
  overagePriceMinor: string;
  overageUnitSize: string;
  /** Whether the plan already carries a row for this service. */
  existing: boolean;
}

function draftsFor(plan: AdminPlan, services: Service[]): GrantDraft[] {
  const byKey = new Map(plan.services.map((grant) => [grant.serviceKey, grant]));
  return services.map((service) => {
    const grant = byKey.get(service.key);
    return {
      serviceKey: service.key,
      name: service.name,
      kind: service.kind,
      unit: service.unit,
      enabled: grant?.enabled ?? false,
      included:
        grant === undefined || grant.includedQuantity === null
          ? ''
          : String(grant.includedQuantity),
      overagePriceMinor:
        grant === undefined || grant.overagePriceMinor === null
          ? ''
          : String(grant.overagePriceMinor),
      overageUnitSize: String(grant?.overageUnitSize ?? 1),
      existing: grant !== undefined,
    };
  });
}

/**
 * What gets sent to the server.
 *
 * Rows the plan never mentioned and that the admin left switched off are
 * dropped: writing an explicit "disabled" row for every service in the catalog
 * would change what the plan claims to cover without anyone asking for it.
 */
function toPayload(drafts: GrantDraft[]): AdminPlanService[] {
  return drafts
    .filter((draft) => draft.enabled || draft.existing)
    .map((draft) => ({
      serviceKey: draft.serviceKey,
      enabled: draft.enabled,
      includedQuantity: draft.included === '' ? null : Number(draft.included),
      overagePriceMinor: draft.overagePriceMinor === '' ? null : Number(draft.overagePriceMinor),
      overageUnitSize: Number(draft.overageUnitSize) || 1,
    }));
}

function invalidGrant(draft: GrantDraft): boolean {
  const nonNegativeInt = (value: string): boolean =>
    value === '' || (Number.isSafeInteger(Number(value)) && Number(value) >= 0);
  return (
    !nonNegativeInt(draft.included) ||
    !nonNegativeInt(draft.overagePriceMinor) ||
    !(Number.isSafeInteger(Number(draft.overageUnitSize)) && Number(draft.overageUnitSize) > 0)
  );
}

function GrantTable({
  drafts,
  currency,
  onChange,
}: {
  drafts: GrantDraft[];
  currency: string;
  onChange: (index: number, patch: Partial<GrantDraft>) => void;
}): ReactNode {
  return (
    <table>
      <thead>
        <tr>
          <th>Service</th>
          <th>Included</th>
          <th>Overage per block</th>
          <th>Block size</th>
          <th>Rate</th>
        </tr>
      </thead>
      <tbody>
        {drafts.map((draft, index) => (
          <tr key={draft.serviceKey} style={draft.enabled ? undefined : { opacity: 0.55 }}>
            <td>
              <label className="row" style={{ gap: '0.4rem' }}>
                <input
                  type="checkbox"
                  checked={draft.enabled}
                  onChange={(e) => onChange(index, { enabled: e.target.checked })}
                  aria-label={`Include ${draft.name}`}
                />
                <span>
                  {draft.name}
                  <span className="faint small mono"> {draft.serviceKey}</span>
                </span>
              </label>
            </td>
            <td>
              {/* A feature has nothing to meter, so a quantity would be noise. */}
              {draft.kind === 'metered' ? (
                <input
                  type="number"
                  min={0}
                  value={draft.included}
                  onChange={(e) => onChange(index, { included: e.target.value })}
                  placeholder="unlimited"
                  disabled={!draft.enabled}
                  aria-label={`Included ${draft.unit} for ${draft.name}`}
                />
              ) : (
                <span className="faint small">n/a</span>
              )}
            </td>
            <td>
              {draft.kind === 'metered' ? (
                <input
                  type="number"
                  min={0}
                  value={draft.overagePriceMinor}
                  onChange={(e) => onChange(index, { overagePriceMinor: e.target.value })}
                  placeholder="not sold"
                  disabled={!draft.enabled}
                  aria-label={`Overage price for ${draft.name}, in minor units`}
                />
              ) : (
                <span className="faint small">n/a</span>
              )}
            </td>
            <td>
              {draft.kind === 'metered' ? (
                <input
                  type="number"
                  min={1}
                  value={draft.overageUnitSize}
                  onChange={(e) => onChange(index, { overageUnitSize: e.target.value })}
                  disabled={!draft.enabled}
                  aria-label={`Block size for ${draft.name}`}
                />
              ) : (
                <span className="faint small">n/a</span>
              )}
            </td>
            <td className="faint small">
              {draft.overagePriceMinor === '' || !draft.enabled
                ? '—'
                : `${formatRate(
                    Number(draft.overagePriceMinor),
                    Number(draft.overageUnitSize) || 1,
                    currency,
                  )} / ${draft.unit}`}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * Edit a live plan's allowances.
 *
 * Permitted in place, unlike a price: entitlements come from each subscriber's
 * own snapshot, so this changes what tenants who subscribe from now on receive
 * and nothing about what anyone already bought. Overage rates are part of it and
 * carry the same rule.
 */
function AllowanceForm({
  plan,
  services,
  onSaved,
}: {
  plan: AdminPlan;
  services: Service[];
  onSaved: () => void;
}): ReactNode {
  const [drafts, setDrafts] = useState<GrantDraft[]>(() => draftsFor(plan, services));
  const { run, pending, error } = useAction();

  const invalid = drafts.some(invalidGrant);

  function change(index: number, patch: Partial<GrantDraft>): void {
    setDrafts((current) =>
      current.map((draft, i) => (i === index ? { ...draft, ...patch } : draft)),
    );
  }

  async function save(): Promise<void> {
    await run(async () => {
      await api.put(`/api/admin/plans/${plan.id}/services`, { services: toPayload(drafts) });
      onSaved();
    });
  }

  if (services.length === 0) {
    return <p className="muted small">The service catalog is empty.</p>;
  }

  return (
    <div className="stack stack-sm" style={{ marginTop: '0.75rem' }}>
      <ErrorBanner message={error} />
      <p className="small faint">
        Applies to tenants who subscribe from now on. Existing subscribers keep the allowances they
        bought. Overage prices are in {plan.currency} minor units per block.
      </p>
      <GrantTable drafts={drafts} currency={plan.currency} onChange={change} />
      <div className="button-row">
        <button
          type="button"
          className="button-primary"
          disabled={pending || invalid}
          onClick={() => void save()}
        >
          {pending && <Spinner />}
          Save allowances
        </button>
      </div>
      {invalid && <p className="field-hint">Quantities must be whole and block size at least 1.</p>}
    </div>
  );
}

/**
 * Publish the next version of a plan.
 *
 * Slug, currency and interval identify the lineage: keeping them supersedes the
 * current version, and changing the slug starts a new plan instead. The version
 * number is the server's to decide, so it is not an input here.
 */
function NewVersionForm({
  plan,
  services,
  onSaved,
}: {
  plan: AdminPlan;
  services: Service[];
  onSaved: () => void;
}): ReactNode {
  const exponent = minorUnitExponent(plan.currency);

  const [slug, setSlug] = useState(plan.slug);
  const [name, setName] = useState(plan.name);
  const [description, setDescription] = useState(plan.description ?? '');
  const [price, setPrice] = useState((plan.basePriceMinor / 10 ** exponent).toFixed(exponent));
  const [contactSalesOnly, setContactSalesOnly] = useState(plan.contactSalesOnly);
  const [drafts, setDrafts] = useState<GrantDraft[]>(() => draftsFor(plan, services));
  const { run, pending, error } = useAction();

  const basePriceMinor = Math.round(Number(price) * 10 ** exponent);
  const priceValid = price !== '' && Number.isSafeInteger(basePriceMinor) && basePriceMinor >= 0;
  const invalid =
    !priceValid || name.trim() === '' || slug.trim() === '' || drafts.some(invalidGrant);
  const supersedes = slug.trim() === plan.slug;

  function change(index: number, patch: Partial<GrantDraft>): void {
    setDrafts((current) =>
      current.map((draft, i) => (i === index ? { ...draft, ...patch } : draft)),
    );
  }

  async function publish(): Promise<void> {
    await run(async () => {
      await api.post('/api/admin/plans', {
        slug: slug.trim(),
        name: name.trim(),
        description: description.trim() === '' ? null : description.trim(),
        currency: plan.currency,
        interval: plan.interval,
        basePriceMinor,
        sortOrder: plan.sortOrder,
        contactSalesOnly,
        services: toPayload(drafts),
      });
      onSaved();
    });
  }

  return (
    <div className="stack stack-sm" style={{ marginTop: '0.75rem' }}>
      <ErrorBanner message={error} />

      <div className={`banner ${supersedes ? 'banner-warning' : ''}`}>
        {supersedes ? (
          <>
            Publishing this will supersede <strong>{plan.name}</strong> v{plan.version}. It stays in
            the catalog as an inactive version and its subscribers are unaffected.
          </>
        ) : (
          <>
            The slug differs from <span className="mono">{plan.slug}</span>, so this publishes a
            separate plan at v1 rather than a new version. Nothing is superseded.
          </>
        )}
      </div>

      <div className="row" style={{ gap: '0.75rem', alignItems: 'flex-end' }}>
        <div style={{ flex: 1 }}>
          <label htmlFor={`nv-name-${plan.id}`}>Name</label>
          <input
            id={`nv-name-${plan.id}`}
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={120}
          />
        </div>
        <div style={{ flex: 1 }}>
          <label htmlFor={`nv-slug-${plan.id}`}>Slug</label>
          <input
            id={`nv-slug-${plan.id}`}
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            maxLength={48}
            className="mono"
          />
        </div>
        <div>
          <label htmlFor={`nv-price-${plan.id}`}>Price ({plan.currency})</label>
          <input
            id={`nv-price-${plan.id}`}
            type="number"
            min={0}
            step="any"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
          />
          <p className="field-hint">per {plan.interval}</p>
        </div>
      </div>

      <div>
        <label htmlFor={`nv-desc-${plan.id}`}>Description</label>
        <input
          id={`nv-desc-${plan.id}`}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={500}
        />
      </div>

      <label className="row" style={{ gap: '0.4rem' }}>
        <input
          type="checkbox"
          checked={contactSalesOnly}
          onChange={(e) => setContactSalesOnly(e.target.checked)}
        />
        <span>
          Quoted plan — the listed amount is a placeholder and tenants cannot subscribe to it
          themselves
        </span>
      </label>

      {services.length > 0 && (
        <GrantTable drafts={drafts} currency={plan.currency} onChange={change} />
      )}

      <div className="button-row">
        <button
          type="button"
          className="button-primary"
          disabled={pending || invalid}
          onClick={() => void publish()}
        >
          {pending && <Spinner />}
          {supersedes ? `Publish v${plan.version + 1}` : 'Publish new plan'}
        </button>
      </div>
      {invalid && (
        <p className="field-hint">
          A name, a slug and a whole-number price are required; block sizes must be at least 1.
        </p>
      )}
    </div>
  );
}
